import { ArabicField } from '../../components/admin/TranslateButton';
import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { eventsAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { PencilIcon, TrashIcon, PlusIcon, EyeIcon } from '@heroicons/react/24/outline';

// ── List ──────────────────────────────────────────────────────
export function AdminEventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = () => {
    eventsAPI.getAll({ status: 'all', limit: 100 }).then(r => setEvents(r.data.data || [])).finally(() => setLoading(false));
  };
  useEffect(fetchEvents, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await eventsAPI.delete(id); toast.success('Event deleted.'); fetchEvents(); }
    catch { toast.error('Failed to delete.'); }
  };

  const statusColor = { published: 'bg-green-100 text-green-700', draft: 'bg-yellow-100 text-yellow-700', cancelled: 'bg-red-100 text-red-700' };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-navy-800">Events</h1>
        <Link to="/admin/events/new" className="btn-primary flex items-center gap-2 py-2 px-4 text-sm">
          <PlusIcon className="h-4 w-4" /> New Event
        </Link>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : events.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No events yet. <Link to="/admin/events/new" className="text-cyan-500 font-medium">Create one →</Link></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>{['Title','Date','Location','Status','Actions'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {events.map(e => (
                  <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-navy-800 max-w-xs">
                      <p className="truncate">{e.title}</p>
                      {e.is_featured && <span className="text-xs text-cyan-500">⭐ Featured</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{format(new Date(e.event_date), 'MMM d, yyyy')}</td>
                    <td className="px-4 py-3 text-gray-500 truncate max-w-[150px]">{e.location || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`badge text-xs capitalize ${statusColor[e.status]}`}>{e.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link to={`/events/${e.slug}`} target="_blank" className="p-1.5 text-gray-400 hover:text-navy-600 rounded-lg hover:bg-gray-100"><EyeIcon className="h-4 w-4" /></Link>
                        <Link to={`/admin/events/${e.id}/edit`} className="p-1.5 text-gray-400 hover:text-navy-600 rounded-lg hover:bg-gray-100"><PencilIcon className="h-4 w-4" /></Link>
                        <button onClick={() => handleDelete(e.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50"><TrashIcon className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ── Form (Create / Edit) ──────────────────────────────────────
const EMPTY = { title: '', title_ar: '', description: '', description_ar: '', event_date: '', end_date: '', location: '', cover_url: '', is_featured: false, status: 'draft' };

export function AdminEventFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    // ✅ Fixed: fetch only the specific event using status=all so admin sees drafts too
    eventsAPI.getAll({ status: 'all', limit: 500 }).then(r => {
      const ev = (r.data.data || []).find(e => String(e.id) === id);
      if (ev) setForm({ ...EMPTY, ...ev, event_date: ev.event_date?.slice(0,16) || '', end_date: ev.end_date?.slice(0,16) || '' });
    });
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) { await eventsAPI.update(id, form); toast.success('Event updated!'); }
      else { await eventsAPI.create(form); toast.success('Event created!'); }
      navigate('/admin/events');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/events" className="text-gray-400 hover:text-navy-600">← Events</Link>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-navy-800">{isEdit ? 'Edit Event' : 'New Event'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="form-label">Title (English) *</label><input required className="form-input" value={form.title} onChange={e => set('title', e.target.value)} /></div>
          <ArabicField label="Title (Arabic)" value={form.title_ar} onChange={v=>set('title_ar',v)} sourceText={form.title} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="form-label">Event Date & Time *</label><input required type="datetime-local" className="form-input" value={form.event_date} onChange={e => set('event_date', e.target.value)} /></div>
          <div><label className="form-label">End Date & Time</label><input type="datetime-local" className="form-input" value={form.end_date} onChange={e => set('end_date', e.target.value)} /></div>
        </div>
        <div><label className="form-label">Location</label><input className="form-input" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Central Mosque, Ibadan" /></div>
        <div><label className="form-label">Cover Image URL</label><input className="form-input" value={form.cover_url} onChange={e => set('cover_url', e.target.value)} placeholder="https://..." /></div>
        <div><label className="form-label">Description (English)</label><textarea rows={4} className="form-input resize-none" value={form.description} onChange={e => set('description', e.target.value)} /></div>
        <ArabicField label="Description (Arabic)" value={form.description_ar} onChange={v=>set('description_ar',v)} sourceText={form.description} multiline rows={4} />
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <input type="checkbox" id="featured" checked={form.is_featured} onChange={e => set('is_featured', e.target.checked)} className="h-4 w-4 accent-cyan-500" />
            <label htmlFor="featured" className="text-sm text-navy-700 font-medium">Mark as Featured</label>
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2 py-2.5 px-6 text-sm">
            {saving ? <><div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> Saving...</> : (isEdit ? 'Update Event' : 'Create Event')}
          </button>
          <Link to="/admin/events" className="btn-outline py-2.5 px-6 text-sm">Cancel</Link>
        </div>
      </form>
    </AdminLayout>
  );
}
